import type { Conversation, RuntimeProfile } from '@deskworks/shared-types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { ArrowRight, Info, MessageSquarePlus, Sparkles, SquareStack, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ConversationThread, useConversationLive } from '@/components/chat/conversation-thread';
import { RelativeTime } from '@/components/relative-time';
import { DictationButton } from '@/components/dictation-button';
import { DraftPanel } from '@/components/setup/draft-panel';
import { AttachButton, AttachmentChips, DropZone, useAttachments } from '@/components/resources/resources';
import { WorkspacePicker } from '@/components/workspace-picker';
import { ErrorState, LoadingPage } from '@/components/states';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { api, errorMessage } from '@/lib/api';
import { findAccount, useDesks, useMe, useRuntimeProfiles, useWorkspace } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';
import { useRealtimeSubscription } from '@/lib/realtime';
import { useToast } from '@/lib/toast';
import { useDictation } from '@/lib/use-dictation';
import { readLastWorkspace } from '@/routes/index-redirect';

const EXAMPLES = [
  'Every customer case should be analysed and answered within 48 hours.',
  'Two blog posts and four LinkedIn posts a week about our product.',
  'Every new lead gets a qualified, personal reply within 2 hours.',
];

/** A runtime that can answer now: the demo runtime, or one with a key / login. */
export const runtimeReady = (p: RuntimeProfile) => p.runtimeType === 'local' || p.hasCredential;

/** Connected real models first, then the scripted demo runtime ("local"), then anything active. */
export function defaultRuntime(profiles: RuntimeProfile[]) {
  const usable = profiles.filter((p) => p.status === 'active' && p.implemented);
  return usable.find((p) => p.runtimeType !== 'local' && runtimeReady(p)) ?? usable.find(runtimeReady) ?? usable[0] ?? null;
}

/** Label suffix in runtime pickers. */
export const runtimeHint = (p: RuntimeProfile) =>
  p.runtimeType === 'local' ? (/demo/i.test(p.name) ? '' : ` ${t('(demo, no AI)')}`) : runtimeReady(p) ? '' : ` ${t('(not connected)')}`;

/**
 * Start page (docs/features/setup-assistant.md): "What do you want to
 * achieve?" → a setup chat whose assistant drafts every change; the person
 * applies the draft.
 */
export function StartPage() {
  const { workspaceId } = useParams({ from: '/_authed/w/$workspaceId/start' });
  const { chat, desk } = useSearch({ from: '/_authed/w/$workspaceId/start' });
  useRealtimeSubscription(workspaceId);
  return chat ? <SetupChat workspaceId={workspaceId} conversationId={chat} /> : <ToFrontDesk workspaceId={workspaceId} deskId={desk} />;
}

/**
 * Setup is one of VICKI's skills (docs/features/front-desk.md#setup): "Create a new desk" and "Change with VICKI" open the
 * Front Desk with the first sentence written.
 */
function ToFrontDesk({ workspaceId, deskId }: { workspaceId: string; deskId?: string }) {
  const navigate = useNavigate();
  const me = useMe();
  const workspace = useWorkspace(workspaceId);
  const desks = useDesks(workspaceId);
  const account = me.data && workspace.data ? me.data.accounts.find((a) => a.id === workspace.data!.accountId) : undefined;
  const desk = deskId ? desks.data?.find((d) => d.id === deskId) : undefined;
  const ready = !!account && (!deskId || !!desks.data);
  useEffect(() => {
    if (!ready) return;
    const message = desk
      ? t('About the desk “{desk}” in {workspace}: ', { desk: desk.name, workspace: workspace.data!.name })
      : t('I need a new desk in {workspace}: ', { workspace: workspace.data!.name });
    void navigate({ to: '/$account/front-desk', params: { account: account!.slug || account!.id }, search: { message }, replace: true });
  }, [ready]); // eslint-disable-line react-hooks/exhaustive-deps
  return <LoadingPage />;
}

/**
 * Setup for the whole account (`/$account/start`): the assistant reads every workspace the person may see and drafts
 * changes for each one they may change; a chat still lives in one workspace (its runtime, files, built-in agent).
 */
export function AccountStartPage() {
  const { account: param } = useParams({ from: '/_authed/$account/start' });
  const { chat, workspace } = useSearch({ from: '/_authed/$account/start' });
  const me = useMe();
  const account = me.data ? findAccount(me.data.accounts, param) : undefined;
  const workspaces = useQuery({ queryKey: qk.accountWorkspaces(account?.id ?? ''), queryFn: () => api.accounts.workspaces(account!.id), enabled: !!account });
  const chatRow = useQuery({ queryKey: qk.conversation(chat ?? ''), queryFn: () => api.conversations.get(chat!), enabled: !!chat });
  const last = readLastWorkspace();
  const home = workspaces.data?.find((w) => w.id === workspace) ?? workspaces.data?.find((w) => w.id === last) ?? workspaces.data?.[0];
  const workspaceId = chat ? (chatRow.data?.workspaceId ?? '') : (home?.id ?? '');
  useRealtimeSubscription(workspaceId);
  if (!account || !workspaceId) return <LoadingPage />;
  const mode = { account: param, accountId: account.id };
  return chat ? <SetupChat workspaceId={workspaceId} conversationId={chat} account={mode} /> : <StartForm workspaceId={workspaceId} account={mode} />;
}

/** On the account's Setup page: the account (as in the address) the links stay in. */
type AccountMode = { account: string; accountId: string };

function StartForm({ workspaceId, focusDeskId, account }: { workspaceId: string; focusDeskId?: string; account?: AccountMode }) {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const profiles = useRuntimeProfiles(workspaceId);
  const desks = useDesks(workspaceId);
  const chats = useQuery<Conversation[]>({
    queryKey: account ? qk.accountSetupConversations(account.accountId) : qk.setupConversations(workspaceId),
    queryFn: () => (account ? api.setup.accountConversations(account.accountId) : api.setup.conversations(workspaceId)),
  });
  const workspaces = useQuery({ queryKey: qk.accountWorkspaces(account?.accountId ?? ''), queryFn: () => api.accounts.workspaces(account!.accountId), enabled: !!account });
  const workspaceName = (id: string) => workspaces.data?.find((w) => w.id === id)?.name;
  const openChat = (id: string) =>
    account
      ? navigate({ to: '/$account/start', params: { account: account.account }, search: { chat: id } })
      : navigate({ to: '/w/$workspaceId/start', params: { workspaceId }, search: { chat: id } });
  const pickWorkspace = (id: string) =>
    account
      ? navigate({ to: '/$account/start', params: { account: account.account }, search: { workspace: id } })
      : navigate({ to: '/w/$workspaceId/start', params: { workspaceId: id }, search: {} });
  const [message, setMessage] = useState('');
  const [runtimeId, setRuntimeId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const dictation = useDictation(message, setMessage, (msg) => toast.error(t('Dictation'), msg));
  // Files go to the workspace right away; the chat takes them over when it starts (docs/features/resources.md).
  const attachments = useAttachments(workspaceId);

  const usable = (profiles.data ?? []).filter((p) => p.status === 'active' && p.implemented);
  const runtime = usable.find((p) => p.id === runtimeId) ?? defaultRuntime(profiles.data ?? []);
  const focusDesk = desks.data?.find((d) => d.id === focusDeskId) ?? null;

  const start = async () => {
    if (!message.trim() || !runtime) return;
    dictation.stop(true);
    setBusy(true);
    try {
      const conv = await api.setup.start(workspaceId, {
        message: message.trim(),
        runtimeProfileId: runtime.id,
        focusDeskId: focusDesk?.id,
        resourceIds: attachments.ids.length ? attachments.ids : undefined,
      });
      void queryClient.invalidateQueries({ queryKey: qk.setupConversations(workspaceId) });
      if (account) void queryClient.invalidateQueries({ queryKey: qk.accountSetupConversations(account.accountId) });
      void openChat(conv.id);
    } catch (err) {
      toast.error(t('Could not start'), errorMessage(err));
      setBusy(false);
    }
  };

  if (profiles.isPending) return <LoadingPage />;
  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-10 sm:px-8 sm:py-16">
      <div className="mb-2 flex items-center gap-2 text-[13px] font-medium text-primary">
        <Sparkles className="size-4" /> <span className="flex-1">{t('Setup assistant')}</span>
        {account && <span className="text-xs font-normal text-muted-foreground">{t('Chat lives in')}</span>}
        <WorkspacePicker workspaceId={workspaceId} onSelect={(id) => void pickWorkspace(id)} />
      </div>
      <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
        {focusDesk
          ? t('What should change on “{desk}”?', { desk: focusDesk.name })
          : account
            ? t('What do you want to achieve?')
            : t('What do you want to achieve in this workspace?')}
      </h1>
      <p className="mt-2 text-sm text-muted-foreground">
        {account
          ? t(
              'Describe it in your own words and attach what helps. The assistant looks at every workspace of the account, uses what is already there — also connections of other workspaces, which it can share — and writes a draft of every change, in whichever workspace it belongs. Nothing changes until you apply the draft.',
            )
          : t(
              'Describe it in your own words and attach what helps — a brand guide, price list, product docs or examples. The assistant looks at what this workspace already has, asks what it needs to know and writes a draft of every change — desks, agents, goals, guidelines, triggers. Nothing changes until you apply the draft.',
            )}
      </p>

      {!usable.length ? (
        <Card className="mt-8 flex flex-col gap-3 p-5 sm:flex-row sm:items-center">
          <div className="flex-1 text-sm">
            <div className="font-medium">{t('Connect a model first')}</div>
            <div className="text-muted-foreground">{t('The assistant needs a model to think with: your Claude or ChatGPT subscription, or an API key.')}</div>
          </div>
          <Button asChild>
            <Link to="/w/$workspaceId/settings" params={{ workspaceId }} search={{ tab: 'runtime-profiles' }}>
              {t('Connect a model')} <ArrowRight />
            </Link>
          </Button>
        </Card>
      ) : (
        <DropZone onFiles={attachments.add} className="mt-8">
          <Card className="p-3">
            {focusDesk && (
              <div className="mb-2 inline-flex items-center gap-1.5 rounded-md bg-muted px-2 py-1 text-xs">
                <SquareStack className="size-3.5" /> {t('About the desk “{desk}”', { desk: focusDesk.name })}
                <Link to="/w/$workspaceId/start" params={{ workspaceId }} search={{}} className="ml-1 text-muted-foreground hover:text-foreground" aria-label={t('Not about this desk')}>
                  <X className="size-3.5" />
                </Link>
              </div>
            )}
            <Textarea
              autoFocus
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void start();
              }}
              rows={4}
              placeholder={focusDesk ? t('e.g. Add a weekly report every Friday') : t('e.g. Every customer case should be analysed and answered within 48 hours.')}
              className="resize-none border-0 text-base shadow-none focus-visible:ring-0 sm:text-[15px]"
            />
            <div className="px-3 pb-2 empty:hidden">
              <AttachmentChips attachments={attachments} />
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-3">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                {t('Model')}
                <Select value={runtime?.id} onValueChange={setRuntimeId}>
                  <SelectTrigger size="sm" className="w-56">
                    <SelectValue placeholder={t('Choose a runtime')} />
                  </SelectTrigger>
                  <SelectContent>
                    {usable.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                        {runtimeHint(p)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-1">
                <AttachButton attachments={attachments} />
                <DictationButton dictation={dictation} />
                <Button onClick={() => void start()} disabled={!message.trim() || attachments.uploading} loading={busy}>
                  {t('Start')} <ArrowRight />
                </Button>
              </div>
            </div>
          </Card>
        </DropZone>
      )}

      {!focusDesk && usable.length > 0 && !message && (
        <div className="mt-4 flex flex-wrap gap-2">
          {EXAMPLES.map((e) => (
            <button key={e} type="button" onClick={() => setMessage(e)} className="rounded-full border px-3 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground">
              {e}
            </button>
          ))}
        </div>
      )}

      {(chats.data?.length ?? 0) > 0 && (
        <div className="mt-12">
          <h2 className="mb-2 text-sm font-medium text-muted-foreground">{t('Earlier setup chats')}</h2>
          <Card className="divide-y">
            {chats.data!.slice(0, account ? 20 : 10).map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => void openChat(c.id)}
                className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm hover:bg-muted/50"
              >
                <span className="min-w-0 flex-1 truncate">{c.title}</span>
                {c.focusDeskName && <span className="truncate text-xs text-muted-foreground">{c.focusDeskName}</span>}
                {account && <span className="truncate text-xs text-muted-foreground">{workspaceName(c.workspaceId)}</span>}
                <RelativeTime className="shrink-0 text-xs text-muted-foreground" value={c.lastMessageAt ?? c.createdAt} />
              </button>
            ))}
          </Card>
        </div>
      )}
    </div>
  );
}

function SetupChat({ workspaceId, conversationId, account }: { workspaceId: string; conversationId: string; account?: AccountMode }) {
  const navigate = useNavigate();
  const { query: conv, live } = useConversationLive(conversationId);
  const attachments = useAttachments(workspaceId, conversationId);
  const [tab, setTab] = useState<'chat' | 'draft'>('chat');
  // Small screens show one pane at a time; the draft pane opens once the assistant drafted something.
  const draftCount = useQuery({ queryKey: qk.setupDraft(conversationId), queryFn: () => api.setup.draft(conversationId) }).data?.draft?.changes.length ?? 0;
  useEffect(() => setTab('chat'), [conversationId]);

  if (conv.isPending) return <LoadingPage />;
  if (conv.isError) return <div className="mx-auto max-w-3xl px-4 py-8"><ErrorState error={conv.error} onRetry={() => void conv.refetch()} /></div>;
  const c = conv.data;
  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex flex-wrap items-center gap-3 border-b bg-background px-4 py-3 sm:px-6">
        <Sparkles className="size-4 text-primary" />
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-base font-semibold">{c.title}</h1>
          <div className="text-xs text-muted-foreground">
            {t('Setup assistant')}{c.runtime ? ` · ${c.runtime}` : ''}
            {c.focusDeskName ? ` · ${t('about “{desk}”', { desk: c.focusDeskName })}` : ''}
          </div>
        </div>
        <div className="inline-flex rounded-md border p-0.5 lg:hidden">
          <Button size="xs" variant={tab === 'chat' ? 'secondary' : 'ghost'} onClick={() => setTab('chat')}>
            {t('Chat')}
          </Button>
          <Button size="xs" variant={tab === 'draft' ? 'secondary' : 'ghost'} onClick={() => setTab('draft')}>
            {draftCount ? t('Draft ({n})', { n: draftCount }) : t('Draft')}
          </Button>
        </div>
        {/* A setup chat lives in its workspace: switching there opens the start page of the other one. */}
        {!account && <WorkspacePicker workspaceId={workspaceId} onSelect={(id) => void navigate({ to: '/w/$workspaceId/start', params: { workspaceId: id }, search: {} })} />}
        <Button asChild size="sm" variant="outline">
          {account ? (
            <Link to="/$account/start" params={{ account: account.account }} search={{}}>
              <MessageSquarePlus /> {t('New')}
            </Link>
          ) : (
            <Link to="/w/$workspaceId/start" params={{ workspaceId }} search={{}}>
              <MessageSquarePlus /> {t('New')}
            </Link>
          )}
        </Button>
      </header>
      <div className="grid min-h-0 flex-1 lg:grid-cols-[minmax(0,1fr)_440px]">
        <ConversationThread
          className={tab === 'chat' ? 'min-h-0' : 'hidden min-h-0 lg:flex'}
          conv={c}
          agentName={c.agentName}
          workspaceId={workspaceId}
          live={live}
          autoFocus
          attachments={attachments}
          placeholder={t('Answer the assistant or ask for changes to the draft …')}
          intro={
            <div className="flex items-start gap-2 rounded-lg border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
              <Info className="mt-0.5 size-3.5 shrink-0" />
              {t('The setup assistant only writes the draft next to this chat. Nothing changes until you click Apply.')}
            </div>
          }
        />
        <DraftPanel conversationId={conversationId} workspaceId={workspaceId} className={tab === 'draft' ? 'min-h-0' : 'hidden min-h-0 lg:flex'} />
      </div>
    </div>
  );
}
